import React, { useContext, useEffect, useState } from 'react';
import './style.css';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import VisibilityIcon from '@material-ui/icons/Visibility';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useHistory, Link } from 'react-router-dom';
import StarsComponent from '../../../components/startComponent/StarsComponent';
import { getFile } from '@opu/utils';
import Text from '../../../components/UI/Text/Text';
import { Chip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../Main';
import AccessControl from '../../../components/monetization/AccessControl';
import { canDownloadBooks, downloadBookPdf } from '../../../helpers/bookDownload';

const BookDetails = (props) => {
  const { isAuth } = useContext(AuthContext);
  const { user } = props;
  const { t } = useTranslation();
  const [moreDetails, setMoreDetails] = useState(false);
  const [book, setBook] = useState();
  const [newRating, setNewRating] = useState(0);
  const maxLength = 280;
  const [newFavorite, setNewFavorite] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const allowDownload = canDownloadBooks(user);

  let history = useHistory();

  useEffect(() => {
    getBook();
  }, []);

  const addToFavorite = () => {
    if (newFavorite) {
      AxiosInstance.delete('/book/remove-from-favorite/' + props.match.params.id)
        .then(() => setNewFavorite(false))
        .catch(() => {});
    } else {
      AxiosInstance.post('/book/add-to-favorite/' + props.match.params.id)
        .then(() => setNewFavorite(true))
        .catch(() => {});
    }
  };

  const getBook = () => {
    AxiosInstance.get(`/book${isAuth ? '' : '/public'}/${props.match.params.id}`).then((response) => {
      setNewFavorite(response.data.bookFavorite);
      setNewRating(response.data.bookReview);
      setBook(response.data);
    });
  };

  const goToBooks = (id, name, from) => {
    let params = new URLSearchParams();
    if (from === 'author') params.append('keyword', name);
    else params.append('categories', id);
    history.push({
      pathname: '/books',
      search: params.toString(),
    });
  };

  const authors = (list) =>
    list.map((author, i) => (
      <React.Fragment key={author.id || author.name}>
        {i > 0 ? ', ' : null}
        <button type="button" className="bd-link" onClick={() => goToBooks(author.id, author.name, 'author')}>
          {author.name}
        </button>
      </React.Fragment>
    ));

  const spec = (label, value) => {
    if (value === null || value === undefined || value === '') return null;
    return (
      <div className="bd-spec" key={label}>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    );
  };

  const fold = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  const abstract = book?.abstract || '';
  const abstractLong = abstract.length > maxLength;
  const showAbstract = abstract && fold(abstract) !== fold(book?.title);

  const categories = (list) =>
    list.map((category) => (
      <Chip
        key={category.id || category.name}
        className="category-chip-container"
        label={category.name}
        onClick={() => goToBooks(category.id, category.name, 'category')}
        variant="outlined"
      />
    ));

  const review = (index) => {
    AxiosInstance.post('/book/add-review/' + props.match.params.id, { rating: index })
      .then(() => setNewRating(index))
      .catch(() => {});
  };

  const viewBook = () => {
    history.push('/view-book/' + props.match.params.id);
  };

  const downloadBook = async () => {
    if (!book?.id || downloading) return;
    setDownloading(true);
    try {
      await downloadBookPdf(book.id, book.title);
    } catch (err) {
      console.error('Book download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="book-details-page">
      <div className="bd-wrap">
        <nav className="bd-crumb" aria-label="breadcrumb">
          <Link to="/">{t('breadcrumb.home')}</Link>
          <span>/</span>
          <Link to="/books">{t('breadcrumb.books')}</Link>
          <span>/</span>
          <span className="is-active">{book && book.title}</span>
        </nav>

        {book && (
          <>
            <section className="bd-hero">
              <div className="bd-cover">
                <img
                  src={getFile('book', book.cover)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/images/book_default.png';
                  }}
                  alt={book.title}
                />
                <button
                  type="button"
                  className={`bd-fav ${newFavorite ? 'is-on' : ''}`}
                  onClick={addToFavorite}
                  aria-label="Favorite"
                >
                  {newFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </button>
              </div>

              <div className="bd-info">
                <div className="bd-cats">{categories(book.categories || [])}</div>
                <h1 className="bd-title">{book.title}</h1>
                <div className="bd-authors">{authors(book.authors || [])}</div>

                <div className="bd-stats">
                  <StarsComponent value={book.rating} onClick={null} />
                  <span className="bd-stat">
                    <VisibilityIcon fontSize="small" />
                    {book.nbrViews}
                  </span>
                  {book.pages ? <span className="bd-stat">{book.pages} {t('books.details.nbrPages')}</span> : null}
                </div>

                <div className="bd-actions">
                  <button type="button" className="bd-read" onClick={viewBook}>
                    {t('books.details.view')}
                  </button>
                  {allowDownload ? (
                    <button type="button" className="bd-download" onClick={downloadBook} disabled={downloading}>
                      {downloading ? t('books.details.downloading') : t('books.details.download')}
                    </button>
                  ) : null}
                  <AccessControl
                    variant="inline"
                    bookId={book.id}
                    bookTitle={book.title}
                    bookPrice={book.price}
                    isAuthenticated={isAuth}
                    userRole={user?.student?.establishment?.name || user?.establishment?.name || 'STUDENT'}
                    user={user}
                    onPurchaseSuccess={() => {}}
                    onSubscriptionSuccess={() => {}}
                  />
                </div>

                {showAbstract ? (
                  <div className="bd-abstract">
                    <p className={moreDetails || !abstractLong ? '' : 'is-clamped'}>{abstract}</p>
                    {abstractLong && (
                      <button type="button" className="bd-more" onClick={() => setMoreDetails(!moreDetails)}>
                        {!moreDetails ? t('books.details.more') : t('books.details.less')}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="bd-panel">
              <h2>{t('book-details')}</h2>
              <dl className="bd-specs">
                {spec(t('books.details.publicationNumber'), book.publicationNumber)}
                {spec(t('books.details.editionNumber'), book.editionNumber)}
                {spec(t('books.details.ISBN'), book.ISBN)}
                {spec(t('books.details.releaseDate'), book.releaseDate ? <Text text={book.releaseDate} type="date" /> : null)}
                {spec(t('books.details.publishingHouse'), book.publishingHouse)}
              </dl>
            </section>

            <section className="bd-panel bd-review">
              <h2>{t('books.details.leaveReview')}</h2>
              <StarsComponent value={newRating} onClick={review} />
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default BookDetails;
